import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

import { posaleService } from '@ft-frontend/core';
import { getSubdomain } from '@ft-frontend/core/src/utils/utils';

import ErrorMessage from 'components/ErrorMessage';

import StartingInfoContext from 'contexts/starting_info/StartingInfoContext';

import { usePosaleAPI } from 'hooks/usePosaleAPI';

import { changeWhitelabel } from 'reducers/redux/reduxSlice';

import { defaultLocale, dynamicActivate } from 'utils/i18nLoaderHelper';

function StartingInfoProvider({ children }) {
  const dispatch = useDispatch();
  const language = useSelector((state) => state.redux.whitelabel.language);

  const { data, isLoading, isError } = usePosaleAPI(
    posaleService.endpoints.getStartingInfo,
    {
      needsWhitelabelUUID: false,
    },
  );
  const [initialLanguage] = useState(language); // we can remove this after migration

  const [appStatus, setAppStatus] = useState('loading');

  useEffect(() => {
    if (!isLoading) {
      const loadLocale = async (locale, status) => {
        await dynamicActivate(locale);

        setAppStatus(status);
      };

      if (!isError) {
        document.title = data.name || 'TD Synnex';
        const language =
          initialLanguage || data.configs?.defaultLanguage || defaultLocale;
        loadLocale(language, 'loaded');

        const subdomain = getSubdomain();
        const domainSplit = subdomain.split('-');
        const shortName = domainSplit[domainSplit.length - 1];
        dispatch(
          changeWhitelabel({
            ...data,
            subdomain,
            shortName,
            configs: {
              ...data.configs,
              onlyUpgradeMaxValue: shortName === 'mediamarktespana',
            },
            language: language,
            // after migration
            //language: data.configs.canChangeLanguage ? data.configs.defaultLanguage || defaultLocale : data.configs.defaultLanguage,
          }),
        );
      } else {
        loadLocale(defaultLocale, 'error');
      }
    }
  }, [data, isError, isLoading, initialLanguage, dispatch]);

  const getContent = () => {
    if (appStatus === 'error') {
      return <ErrorMessage />;
    } else if (appStatus === 'loading') {
      return <React.Fragment />;
    } else {
      return children;
    }
  };

  let materialTheme = {};

  try {
    materialTheme = JSON.parse(data?.configs?.materialTheme || '{}');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return (
    <StartingInfoContext.Provider value={data}>
      <ThemeProvider
        theme={(defaultTheme) =>
          createTheme(deepmerge(defaultTheme, materialTheme))
        }
      >
        {getContent()}
      </ThemeProvider>
    </StartingInfoContext.Provider>
  );
}

export default StartingInfoProvider;
